import React, { useState } from 'react';
import { Sparkles, Search, X, Loader2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { modelServicePost } from '../../utils/modelService';

const PLACEHOLDERS = {
  properties: 'Try: "properties in Georgia with no contact" or "overdue and unassigned"',
  outreach: 'Try: "follow-ups sent to Georgia" or "emails that failed"',
  review: 'Try: "properties with corrections pending" or "approved files in Missouri"',
  client: 'Try: "properties that need attention" or "completed in January"',
  contacts: 'Try: "unresponsive contacts with more than 10 units" or "paused contacts"',
  director: 'Try: "lapsed clients worth over $5000" or "clients not logging in for 6 months"',
};

const SmartFilter = ({ onApplyFilters, audience = 'internal', context = 'properties' }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [interpreted, setInterpreted] = useState('');
  const [active, setActive] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setInterpreted('');

    try {
      const res = await modelServicePost('/audit/ai/smart-filter', { query: query.trim(), audience, context });

      if (res.ok) {
        const data = await res.json();
        if (data.filters && Object.keys(data.filters).length > 0) {
          onApplyFilters(data.filters);
          setInterpreted(data.interpreted);
          setActive(true);
        } else {
          setInterpreted('No matching filter found. Try being more specific.');
        }
      }
    } catch {
      setInterpreted('Could not connect to JPro AI');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setInterpreted('');
    setActive(false);
    onApplyFilters({});
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <div className={`relative flex-1 ${active ? (d ? 'ring-1 ring-violet-500/50 rounded-lg' : 'ring-1 ring-violet-400/50 rounded-lg') : ''}`}>
          <Sparkles size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${active ? 'text-violet-500' : (d ? 'text-gray-500' : 'text-gray-400')}`} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && handleSearch()}
            placeholder={PLACEHOLDERS[context] || PLACEHOLDERS.properties}
            className={`w-full pl-8 pr-3 py-2 rounded-lg border text-[13px] font-rasa ${
              d ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
            } focus:outline-none focus:ring-1 focus:ring-violet-500/50`}
          />
        </div>
        {loading ? (
          <div className="p-2"><Loader2 size={16} className="animate-spin text-violet-500" /></div>
        ) : (
          <button
            onClick={handleSearch}
            disabled={!query.trim()}
            className="px-3 py-2 rounded-lg text-[12px] font-rasa font-medium bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 disabled:opacity-40"
          >
            <Search size={14} />
          </button>
        )}
        {active && (
          <button onClick={handleClear} className={`p-2 rounded-lg ${d ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X size={14} />
          </button>
        )}
      </div>
      {interpreted && (
        <div className={`flex items-center gap-1.5 px-2 text-[11px] font-rasa ${active ? 'text-violet-500' : (d ? 'text-gray-500' : 'text-gray-400')}`}>
          <Sparkles size={10} /> {interpreted}
        </div>
      )}
    </div>
  );
};

export default SmartFilter;
